import React, { useEffect, useId, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import "./ToolPrepDownloadThankYouModal.css";

const MY_DESIGNS_PATH = "/my-designs";

const DEFAULT_COPY = {
  titleDownloading: "Downloading…",
  titleComplete: "Download complete",
  titleError: "Download failed",
  leadDownloading: "Please wait — we’re saving your file to this device.",
  leadComplete:
    "Your file is saved on this computer or phone. You can find it in your Downloads folder.",
  leadError: "Something went wrong and the file wasn’t saved. Please try again.",
  previewAlt: "Download preview",
  primaryActionLabel: "Add new image",
  secondaryActionLabel: "Share with friends",
  secondaryCopiedLabel: "Link copied!",
  closeLabelDownloading: "Close",
  closeLabelComplete: "Close",
  myDesignsLabel: "Stored in",
  myDesignsSaving: "Saving…",
  myDesignsOpen: "Open",
  backToDashboardLabel: "Back to Dashboard",
};

const StatusIcon = ({ phase }) => {
  if (phase === "complete") {
    return (
      <span className="tpt-thankyou__status-icon tpt-thankyou__status-icon--done" aria-hidden="true">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path
            d="M5 13l4 4L19 7"
            stroke="currentColor"
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    );
  }
  if (phase === "error") {
    return (
      <span className="tpt-thankyou__status-icon tpt-thankyou__status-icon--error" aria-hidden="true">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path
            d="M12 8v5M12 16h.01M12 3a9 9 0 100 18 9 9 0 000-18z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </span>
    );
  }
  return (
    <span className="tpt-thankyou__status-icon tpt-thankyou__status-icon--busy" aria-hidden="true">
      <svg
        className="tpt-thankyou__spinner"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
      >
        <circle
          cx="12"
          cy="12"
          r="9"
          stroke="currentColor"
          strokeOpacity="0.25"
          strokeWidth="3"
        />
        <path
          d="M21 12a9 9 0 00-9-9"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
        />
      </svg>
    </span>
  );
};

/**
 * Shared download toast — image preview + progress + success in one card.
 */
const ToolPrepDownloadThankYouModal = ({
  open = false,
  progress = 0,
  phase = "downloading",
  errorMessage = "",
  imageUrl = null,
  onClose = () => {},
  onFinish = () => {},
  onPrimaryAction = () => {},
  onMyDesigns = () => {},
  onShare = () => {},
  shareCopied = false,
  showMyDesigns = false,
  onBackToDashboard = null,
  copy = {},
}) => {
  const titleId = useId();
  const [mounted, setMounted] = useState(false);
  const text = { ...DEFAULT_COPY, ...copy };

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape" || phase === "downloading") return;
      if (phase === "complete") onFinish();
      else onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, phase, onClose, onFinish]);

  if (!mounted || !open) return null;

  const clampedProgress = Math.min(100, Math.max(0, progress));
  const isDownloading = phase === "downloading";
  const isComplete = phase === "complete";
  const isError = phase === "error";

  const title = isError
    ? text.titleError
    : isComplete
      ? text.titleComplete
      : text.titleDownloading;

  const lead = isError
    ? errorMessage || text.leadError
    : isComplete
      ? text.leadComplete
      : text.leadDownloading;

  return createPortal(
    <div className="tpt-thankyou" role="presentation">
      <div
        className={`tpt-thankyou__card${isComplete ? " is-complete" : ""}${
          isDownloading ? " is-downloading" : ""
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-busy={isDownloading}
      >
        <button
          type="button"
          className="tpt-thankyou__close"
          aria-label={
            isDownloading ? text.closeLabelDownloading : text.closeLabelComplete
          }
          disabled={isDownloading}
          onClick={isComplete ? onFinish : onClose}
        >
          ×
        </button>

        <div className="tpt-thankyou__preview">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={text.previewAlt}
              className="tpt-thankyou__preview-img"
              draggable={false}
            />
          ) : (
            <div className="tpt-thankyou__preview-empty" />
          )}
        </div>

        <div className="tpt-thankyou__body">
          <h2 id={titleId} className="tpt-thankyou__title">
            <StatusIcon phase={phase} />
            <span>{title}</span>
          </h2>
          <p
            className={`tpt-thankyou__lead${
              isError ? " tpt-thankyou__lead--error" : ""
            }`}
          >
            {lead}
          </p>

          {!isError ? (
            <div
              className={`tpt-thankyou__progress${
                isComplete ? " is-complete" : ""
              }`}
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={clampedProgress}
              aria-label={
                isComplete ? "Download complete" : "Download progress"
              }
            >
              <div className="tpt-thankyou__progress-track">
                <div
                  className="tpt-thankyou__progress-fill"
                  style={{ width: `${clampedProgress}%` }}
                >
                  {isDownloading ? (
                    <span className="tpt-thankyou__progress-sheen" />
                  ) : null}
                </div>
              </div>
              <span className="tpt-thankyou__progress-pct">
                {isComplete ? "Done" : `${clampedProgress}%`}
              </span>
            </div>
          ) : null}

          <div className="tpt-thankyou__actions">
            {isError ? (
              <button
                type="button"
                className="tpt-thankyou__done-btn"
                onClick={onClose}
              >
                Close
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="tpt-thankyou__share-btn"
                  onClick={onPrimaryAction}
                  disabled={!isComplete}
                >
                  {text.primaryActionLabel}
                </button>
                <button
                  type="button"
                  className="tpt-thankyou__done-btn"
                  onClick={onShare}
                  disabled={!isComplete}
                >
                  {shareCopied
                    ? text.secondaryCopiedLabel
                    : text.secondaryActionLabel}
                </button>
              </>
            )}
          </div>

          {!isError && typeof onBackToDashboard === "function" ? (
            <button
              type="button"
              className="tpt-thankyou__dashboard-btn"
              onClick={onBackToDashboard}
              disabled={!isComplete}
            >
              {text.backToDashboardLabel}
            </button>
          ) : null}

          {!isError && showMyDesigns ? (
            <div className="tpt-thankyou__util">
              <span className="tpt-thankyou__util-text">
                {text.myDesignsLabel}{" "}
                {isComplete ? (
                  <Link
                    to={MY_DESIGNS_PATH}
                    className="tpt-thankyou__util-link"
                    onClick={(e) => {
                      e.stopPropagation();
                      onMyDesigns();
                    }}
                  >
                    My Designs
                  </Link>
                ) : (
                  <strong>My Designs</strong>
                )}
              </span>
              {isComplete ? (
                <Link
                  to={MY_DESIGNS_PATH}
                  className="tpt-thankyou__util-go"
                  onClick={(e) => {
                    e.stopPropagation();
                    onMyDesigns();
                  }}
                >
                  {text.myDesignsOpen}
                </Link>
              ) : (
                <span className="tpt-thankyou__util-wait">
                  {text.myDesignsSaving}
                </span>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ToolPrepDownloadThankYouModal;
